import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
  buildFAQPageSchema,
} from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { MessageCircle, Bot, Clock, Zap, ArrowRight } from "lucide-react";

const TITLE = "Agente de IA para WhatsApp Business para vendas e atendimento | FabChat";
const DESCRIPTION =
  "Agente de IA para WhatsApp Business com CRM integrado: automatize atendimento, qualifique leads e organize follow-up em um único painel.";

const BREADCRUMB = [
  { name: "Início", path: "/" },
  { name: "Agente de IA para WhatsApp", path: "/whatsapp/agente-ia" },
];

const FAQ_ITEMS = [
  {
    question: "O que é um agente de IA para WhatsApp?",
    answer:
      "É um assistente de inteligência artificial conectado ao WhatsApp Business que conversa com o cliente de forma natural, mantém o contexto e segue regras configuradas pela empresa. Diferente de fluxos fixos, o agente entende a intenção e responde — sendo hoje o novo método para construir relacionamento com o cliente em escala, com tom de voz próprio da marca.",
  },
  {
    question: "Como criar um agente de IA para WhatsApp?",
    answer:
      "Na FabChat o processo é sem código: você conecta o seu número do WhatsApp Business, define a personalidade do agente, os horários, as regras de encaminhamento para humano e a base de conhecimento. Em poucos minutos o agente está respondendo, qualificando leads e organizando os contatos no CRM integrado.",
  },
  {
    question: "Quanto custa um agente de IA para WhatsApp?",
    answer:
      "A FabChat trabalha com planos mensais (Essential, Plus e PRO) que variam conforme o volume de conversas, número de agentes e recursos como automações e integrações. Os valores atualizados ficam na página de planos da FabChat.",
  },
  {
    question: "Qual a diferença entre agente de IA e um chatbot tradicional?",
    answer:
      "Um chatbot tradicional segue fluxos fixos (árvores de decisão) e responde com mensagens prontas. Um agente de IA interpreta o contexto, mantém o histórico da conversa, adapta a resposta dentro das regras configuradas e encaminha para um humano quando necessário. Por isso o agente de IA é considerado a nova forma de relacionamento com o cliente — mais próximo de um atendente real e menos de um robô engessado.",
  },
  {
    question: "O agente de IA da FabChat substitui o atendimento humano?",
    answer:
      "Não. O agente é um assistente que escala o primeiro contato, responde dúvidas frequentes, qualifica leads e agenda compromissos. Sempre que faz sentido — perguntas complexas, negociação ou suporte sensível — ele encaminha a conversa para o time, com o histórico completo disponível no painel.",
  },
];

export default function WhatsAppAgenteIa() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, BREADCRUMB);
  const organizationSchema = buildOrganizationSchema(baseUrl);
  const softwareSchema = buildSoftwareApplicationSchema(baseUrl, DESCRIPTION);
  const faqSchema = buildFAQPageSchema(FAQ_ITEMS);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead title={TITLE} description={DESCRIPTION} canonicalPath="/whatsapp/agente-ia" />
      <StructuredData
        data={[organizationSchema, softwareSchema, breadcrumbSchema, faqSchema]}
      />
      <Header />

      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Agente de IA para WhatsApp</h1>
          <p className="text-lg text-muted-foreground mb-12">
            Agente de IA para WhatsApp Business com foco em atendimento e vendas: respostas automáticas com contexto, regras configuráveis e integração ao CRM da operação.
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">O que é um agente de IA no WhatsApp</h2>
            <p className="text-muted-foreground mb-4">
              Na FabChat, o agente de IA é um <strong className="text-foreground">assistente conversacional</strong> conectado ao seu WhatsApp Business. Ele responde com base em <strong className="text-foreground">regras e personalidade</strong> que você define: tom de voz, horários de funcionamento, respostas a dúvidas frequentes e encaminhamento para um humano quando fizer sentido. O <strong className="text-foreground">comportamento é totalmente configurável</strong> pela plataforma, sem código — você controla contexto e limites do que o agente faz.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Recursos para atendimento automático no WhatsApp</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Envio e recebimento de mensagens, com suporte a texto e mídia (imagens, vídeos, áudios).</span>
              </li>
              <li className="flex gap-3">
                <Bot className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Respostas humanizadas e <strong className="text-foreground">contexto da conversa</strong> mantido pela IA.</span>
              </li>
              <li className="flex gap-3">
                <Clock className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span><strong className="text-foreground">Horários configuráveis:</strong> defina quando o agente atende e quando encaminha ou informa que está fora do expediente.</span>
              </li>
              <li className="flex gap-3">
                <Zap className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Follow-up automático e gestão de leads no mesmo painel, com histórico de conversas.</span>
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Para quem busca automação de atendimento no WhatsApp</h2>
            <p className="text-muted-foreground mb-4">
              Qualquer negócio que use WhatsApp para vendas, suporte ou agendamentos e queira escalar o primeiro contato sem perder qualidade. A plataforma permite conectar o número oficial ao agente, configurar personalidade e regras e acompanhar métricas no dashboard.
            </p>
          </section>

          <p className="text-sm text-muted-foreground mb-6">
            Também temos página para quem busca <Link to="/whatsapp/chatbot" className="text-primary hover:underline">chatbot para WhatsApp</Link>; no mesmo painel você pode usar <Link to="/instagram/automacao-dm" className="text-primary hover:underline">automação para Instagram</Link> e o <Link to="/integracoes/crm" className="text-primary hover:underline">CRM integrado</Link>.
          </p>

          <section className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button asChild size="lg" className="gap-2">
              <Link to="/planos">Ver planos <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contato">Falar com a gente</Link>
            </Button>
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}
